import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getProductLinks, addProductLink, removeProductLink } from "@/lib/api";
import { toast } from "sonner";

type ProductLinksProps = {
  site: { slug: string };
  product: { id: number };
};

const ProductLinks: React.FC<ProductLinksProps> = ({ site, product }) => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkType, setLinkType] = useState<string>("url");
  const [linkTitle, setLinkTitle] = useState("");

  const {
    data: links,
    isLoading: linksLoading,
    isError: linksError,
    error: linksErrorObj,
  } = useQuery({
    queryKey: ["product-links", site.slug, product.id],
    queryFn: () => getProductLinks(site.slug, product.id),
    enabled: !!product,
  });

  const addLinkMutation = useMutation({
    mutationFn: async (data: { url: string; link_type: string; title: string }) => {
      return addProductLink(site.slug, product.id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-links", site.slug, product.id] });
      setLinkUrl("");
      setLinkType("url");
      setLinkTitle("");
      setShowAddDialog(false);
      toast("Link added", {
        description: "The link was added successfully.",
      });
    },
    onError: (error: any) => {
      toast("Failed to add link", {
        description: error?.message || "Failed to add link.",
      });
    },
  });

  const removeLinkMutation = useMutation({
    mutationFn: async (linkId: number) => {
      return removeProductLink(site.slug, product.id, linkId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-links", site.slug, product.id] });
      toast("Link removed", {
        description: "The link was removed successfully.",
      });
    },
  });

  const handleAddLink = () => {
    if (!linkUrl || !linkType) {
      toast("Missing information", {
        description: "Please fill in all required fields.",
      });
      return;
    }
    addLinkMutation.mutate({
      url: linkUrl,
      link_type: linkType,
      title: linkTitle,
    });
  };

  const getLinkTypeIcon = (type: string) => {
    switch (type) {
      case "youtube":
        return "📺";
      case "vimeo":
        return "🎥";
      case "url":
        return "🔗";
      default:
        return "🔗";
    }
  };

  const getEmbedUrl = (link: any) => {
    if (link.link_type === "youtube") {
      // Extract video ID from various YouTube URL formats
      const videoId = link.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    } else if (link.link_type === "vimeo") {
      // Extract video ID from Vimeo URL
      const videoId = link.url.match(/vimeo\.com\/(\d+)/)?.[1];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }
    return null;
  };

  return (
    <div>
      <div className="mb-4">
        <Button onClick={() => setShowAddDialog(true)} className="mb-2">
          Add Link
        </Button>
        <p className="text-sm text-muted-foreground">
          Add video tutorials or external resources related to your product
        </p>
      </div>

      {linksLoading ? (
        <div className="text-muted-foreground">Loading links...</div>
      ) : linksError ? (
        <Alert variant="destructive" className="mb-2">
          {(linksErrorObj as Error).message}
        </Alert>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {links && links.length > 0 ? (
            links.map((link: any) => {
              const embedUrl = getEmbedUrl(link);
              return (
                <Card key={link.id} className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{getLinkTypeIcon(link.link_type)}</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{link.title || link.url}</h3>
                      <div className="text-xs text-muted-foreground mt-2">
                        <span className="mr-4">Type: {link.link_type}</span>
                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {link.url}
                        </a>
                      </div>
                      {embedUrl && (
                        <div className="mt-4 aspect-video w-full max-w-2xl">
                          <iframe
                            src={embedUrl}
                            className="w-full h-full rounded"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-300"
                        onClick={() => removeLinkMutation.mutate(link.id)}
                        disabled={removeLinkMutation.isPending}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <div className="text-muted-foreground text-center py-8">
              No links added yet.
            </div>
          )}
        </div>
      )}

      {/* Add Link Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg w-full">
          <DialogHeader>
            <DialogTitle>Add Product Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="link-type">* Link Type</Label>
              <Select value={linkType} onValueChange={setLinkType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select link type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="url">URL</SelectItem>
                  <SelectItem value="youtube">YouTube</SelectItem>
                  <SelectItem value="vimeo">Vimeo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="link-title">Title (optional)</Label>
              <Input
                id="link-title"
                value={linkTitle}
                onChange={(e) => setLinkTitle(e.target.value)}
                placeholder="Enter link title"
              />
            </div>
            <div>
              <Label htmlFor="link-url">* URL</Label>
              <Input
                id="link-url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder={linkType === "youtube" ? "https://www.youtube.com/watch?v=..." : linkType === "vimeo" ? "https://vimeo.com/..." : "https://example.com"}
                required
              />
            </div>
          </div>
          <DialogFooter className="mt-4 flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setLinkUrl("");
                setLinkType("url");
                setLinkTitle("");
              }}
              disabled={addLinkMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleAddLink} disabled={addLinkMutation.isPending || !linkUrl}>
              {addLinkMutation.isPending ? "Adding..." : "Add Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductLinks;
